import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { motion } from 'framer-motion';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';

const schema = yup.object({
  name: yup.string().required('Name is required').min(2, 'Name must be at least 2 characters'),
  email: yup.string().required('Email is required').email('Invalid email format'),
  company: yup.string().required('Company name is required'),
  phone: yup.string().matches(/^[\+]?[1-9][\d]{0,15}$/, 'Invalid phone number'),
  isoStandards: yup.array().min(1, 'Please select at least one ISO standard'),
  companySize: yup.string().required('Please select company size'),
  message: yup.string().required('Message is required').min(10, 'Message must be at least 10 characters')
});

const ContactForm = () => {
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset, watch } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      isoStandards: []
    }
  });

  const onSubmit = async (data) => {
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    console.log('Form submitted:', data);
    alert('Thank you! We\'ll contact you within 24 hours.');
    reset();
  };

  const isoOptions = [
    { value: '9001', label: 'ISO 9001 - Quality Management' },
    { value: '14001', label: 'ISO 14001 - Environmental Management' },
    { value: '27001', label: 'ISO 27001 - Information Security' },
    { value: '45001', label: 'ISO 45001 - Occupational Health & Safety' },
    { value: '22000', label: 'ISO 22000 - Food Safety' },
    { value: '50001', label: 'ISO 50001 - Energy Management' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
      className="bg-white p-8 rounded-3xl shadow-2xl"
    >
      <h3 className="text-3xl font-bold mb-6 text-gray-800">Get Your Free Consultation</h3>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Full Name *
            </label>
            <input
              {...register('name')}
              className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 focus:outline-none ${
                errors.name 
                  ? 'border-red-500 focus:border-red-600' 
                  : 'border-gray-200 focus:border-blue-500'
              }`}
              placeholder="John Doe"
            />
            {errors.name && (
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-red-500 text-sm mt-1 flex items-center gap-1"
              >
                <AlertCircle className="w-4 h-4" />
                {errors.name.message}
              </motion.p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              {...register('email')}
              type="email"
              className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 focus:outline-none ${
                errors.email 
                  ? 'border-red-500 focus:border-red-600' 
                  : 'border-gray-200 focus:border-blue-500'
              }`}
              placeholder="john@company.com"
            />
            {errors.email && (
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-red-500 text-sm mt-1 flex items-center gap-1"
              >
                <AlertCircle className="w-4 h-4" />
                {errors.email.message}
              </motion.p>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Company Name *
            </label>
            <input
              {...register('company')}
              className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 focus:outline-none ${
                errors.company 
                  ? 'border-red-500 focus:border-red-600' 
                  : 'border-gray-200 focus:border-blue-500'
              }`}
              placeholder="Company Inc."
            />
            {errors.company && (
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-red-500 text-sm mt-1 flex items-center gap-1"
              >
                <AlertCircle className="w-4 h-4" />
                {errors.company.message}
              </motion.p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              {...register('phone')}
              type="tel"
              className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 focus:outline-none ${
                errors.phone 
                  ? 'border-red-500 focus:border-red-600' 
                  : 'border-gray-200 focus:border-blue-500'
              }`}
              placeholder="+44 7700 900 123"
            />
            {errors.phone && (
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-red-500 text-sm mt-1 flex items-center gap-1"
              >
                <AlertCircle className="w-4 h-4" />
                {errors.phone.message}
              </motion.p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Company Size *
          </label>
          <select
            {...register('companySize')}
            className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 focus:outline-none ${
              errors.companySize 
                ? 'border-red-500 focus:border-red-600' 
                : 'border-gray-200 focus:border-blue-500'
            }`}
          >
            <option value="">Select company size</option>
            <option value="1-10">1-10 employees</option>
            <option value="11-50">11-50 employees</option>
            <option value="51-200">51-200 employees</option>
            <option value="201-1000">201-1000 employees</option>
            <option value="1000+">1000+ employees</option>
          </select>
          {errors.companySize && (
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-red-500 text-sm mt-1 flex items-center gap-1"
            >
              <AlertCircle className="w-4 h-4" />
              {errors.companySize.message}
            </motion.p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            ISO Standards of Interest * (Select all that apply)
          </label>
          <div className="grid md:grid-cols-2 gap-3">
            {isoOptions.map((option) => (
              <label key={option.value} className="flex items-center space-x-3 p-3 rounded-xl border-2 border-gray-200 hover:border-blue-300 transition-all cursor-pointer">
                <input
                  type="checkbox"
                  value={option.value}
                  {...register('isoStandards')}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{option.label}</span>
              </label>
            ))}
          </div>
          {errors.isoStandards && (
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-red-500 text-sm mt-1 flex items-center gap-1"
            >
              <AlertCircle className="w-4 h-4" />
              {errors.isoStandards.message}
            </motion.p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Project Details *
          </label>
          <textarea
            {...register('message')}
            rows="4"
            className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 focus:outline-none resize-none ${
              errors.message 
                ? 'border-red-500 focus:border-red-600' 
                : 'border-gray-200 focus:border-blue-500'
            }`}
            placeholder="Tell us about your ISO implementation goals, current systems, and timeline..."
          />
          {errors.message && (
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-red-500 text-sm mt-1 flex items-center gap-1"
            >
              <AlertCircle className="w-4 h-4" />
              {errors.message.message}
            </motion.p>
          )}
        </div>

        <motion.button
          type="submit"
          disabled={isSubmitting}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white py-4 px-6 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 transition-all duration-300 disabled:opacity-50"
        >
          {isSubmitting ? (
            <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
          ) : (
            <>
              <Send className="w-5 h-5" />
              Send Message
            </>
          )}
        </motion.button>
      </form>
    </motion.div>
  );
};

export default ContactForm;